import { useEffect, useRef, type RefObject } from 'react'
import { Link } from 'react-router-dom'
import { useFocusTrap } from '@/hooks/useFocusTrap'
import { publicModeConfig, type PublicMode } from './publicNav'
import { DrawerNavContent } from './DrawerNavContent'
import styles from './PublicMobileSheet.module.css'

type Props = {
  open: boolean
  onClose: () => void
  mode: Exclude<PublicMode, 'auth' | 'app'>
  returnFocusRef: RefObject<HTMLElement | null>
}

/**
 * Mobile navigation sheet for both public header modes — a top-anchored sheet
 * (not the admin sidebar drawer). Mirrors AppDrawer's dialog mechanics:
 * always-mounted root, aria-hidden when closed, focus trap, ESC + backdrop
 * close, focus-in to the close button and focus-return to the hamburger. Route
 * -change close lives in PublicHeader (which owns `open`).
 *
 * The menu CONTENT is the shared DrawerNavContent — the exact same polished,
 * icon'd, active-aware menu the in-app AppDrawer renders — so the public and
 * in-app menus can't drift (owner feedback 2026-07-22: one menu everywhere).
 * Only the per-mode signed-out CTA is passed through.
 */
export function PublicMobileSheet({ open, onClose, mode, returnFocusRef }: Props) {
  const config = publicModeConfig(mode)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const closeButtonRef = useRef<HTMLButtonElement | null>(null)

  useFocusTrap(open, panelRef)

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  useEffect(() => {
    if (open) closeButtonRef.current?.focus()
    else returnFocusRef.current?.focus()
  }, [open, returnFocusRef])

  return (
    <div
      className={`${styles.sheetRoot} ${open ? styles.sheetRootOpen : ''}`.trim()}
      aria-hidden={!open}
    >
      <div className={styles.sheetBackdrop} role="presentation" onClick={onClose} />
      <div
        ref={panelRef}
        className={styles.sheetPanel}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
      >
        <div className={styles.sheetHead}>
          <Link to={config.brandTo} className={styles.sheetBrand} onClick={onClose}>
            The Racket Center
          </Link>
          <button
            type="button"
            ref={closeButtonRef}
            className={styles.sheetClose}
            aria-label="Close navigation"
            onClick={onClose}
          >
            <span aria-hidden="true">✕</span>
          </button>
        </div>

        {/* Public header: the avatar dropdown (PublicAccountMenu) carries Manage +
            Account at EVERY width, so the sheet drops those groups and stays pure
            app-navigation — no menu-vs-avatar duplication. The in-app AppDrawer,
            by contrast, leaves the prop default (false) because its top-bar avatar
            is only a /profile jump, so the drawer is the sole account reach.

            THE COMPANY GROUP IS THIS SHEET'S ALONE, and only in marketing mode
            (owner, 2026-09-07). It is the phone's copy of the header's marketing
            bar — that bar renders on marketing surfaces only, so its stand-in must
            too, or the same links the owner took off /clubs simply reappear there
            one tap down. On a player surface those routes stay reachable from the
            footer's link row instead. */}
        <DrawerNavContent
          company={mode === 'marketing'}
          onNavigate={onClose}
          signedOut={{
            signInTo: config.signInTo,
            // Same CTA as the header's, but this IS the drawer — re-source it so
            // funnel attribution separates a hamburger click from a header click.
            primary: config.signedOutPrimary.ctaSource
              ? { ...config.signedOutPrimary, ctaSource: 'drawer' }
              : config.signedOutPrimary,
          }}
          hideAccountGroups
        />
      </div>
    </div>
  )
}
